/**
 * features/terms/tabs/NoticesTab.tsx
 * ------------------------------------------------------------------
 * Per-(year, term) report notices. Lifted from ReportNoticesPanel
 * which showed all three terms stacked vertically; here scoped to
 * one term, side-by-side front + back inputs.
 *
 * Plain text only; line breaks preserved. Empty fields = silent
 * absence on the report card (no orphaned headings).
 * ------------------------------------------------------------------ */
import { useEffect, useState } from 'react';
import { doc } from 'firebase/firestore';
// Batch C: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Save, Check, AlertTriangle, Loader2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { SCHOOL, type TermKey } from '@/lib/school.config';
import { getReportNotice, validateNotice } from '@/lib/assessmentConfig';
import { Card } from '@/components/ui/primitives';

interface Props {
  year: string;
  term: TermKey;
}

export default function NoticesTab({ year, term }: Props) {
  const { userData } = useAuth();
  const { reportNotices } = useData();
  const [draft, setDraft] = useState<{ frontPage: string; backPage: string }>({
    frontPage: '',
    backPage: '',
  });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  useEffect(() => {
    setDraft(getReportNotice(year, term, reportNotices));
  }, [reportNotices, year, term]);

  async function save() {
    const front = draft.frontPage;
    const back = draft.backPage;
    const vF = validateNotice(front);
    const vB = validateNotice(back);
    if (!vF.ok || !vB.ok) {
      setMsg({ ok: false, text: [...vF.errors, ...vB.errors].join(' · ') });
      return;
    }
    setBusy(true);
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: 'Saving notices…' });
    try {
      const id = `${year}_${term}`;
      await setDoc(
        doc(db, COLLECTIONS.reportNotices, id),
        {
          id,
          year,
          term,
          frontPage: front.trim(),
          backPage: back.trim(),
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid ?? '',
        },
        { merge: true },
      );
      setSaveModal({ show: true, status: 'success', message: `${SCHOOL.terms[term].label} ${year} notices saved.` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
        Report notices
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">
        Two short text blocks that print on this term's report card. The <strong>front-page</strong> block appears below the student detail grid on the cover; the <strong>back-page</strong> block replaces the generic announcements on the last page. Empty fields print nothing (no orphaned headings).
      </p>
      <p className="text-xs text-gray-400 mb-4">
        Max 500 characters each. Plain text only — line breaks are preserved but no bold/italic/links.
      </p>

      {msg && (
        <div className={`mb-4 text-sm rounded-xl px-3 py-2.5 border flex items-center gap-2 ${
          msg.ok
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-700'
            : 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-700'
        }`}>
          {msg.ok ? <Check size={15} /> : <AlertTriangle size={15} />}
          {msg.text}
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Front-page notice
          </label>
          <textarea
            value={draft.frontPage}
            onChange={(e) => setDraft((d) => ({ ...d, frontPage: e.target.value }))}
            rows={5}
            maxLength={500}
            placeholder="Short reminder for the cover (e.g. parent meeting date). Leave blank for none."
            className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all resize-y"
          />
          <p className="text-[10px] text-gray-400 mt-1">{draft.frontPage.length}/500</p>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
            Back-page notice
          </label>
          <textarea
            value={draft.backPage}
            onChange={(e) => setDraft((d) => ({ ...d, backPage: e.target.value }))}
            rows={5}
            maxLength={500}
            placeholder="Longer announcement for the back cover (e.g. fee deadlines, next-term dates). Leave blank for none."
            className="w-full px-3 py-2 rounded-xl border-2 border-gray-200 dark:border-gray-700 dark:bg-gray-800 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all resize-y"
          />
          <p className="text-[10px] text-gray-400 mt-1">{draft.backPage.length}/500</p>
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
        <button
          onClick={save}
          disabled={busy}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-primary text-white text-sm font-semibold hover:bg-primary/90 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          Save {SCHOOL.terms[term].label} notices
        </button>
      </div>
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </Card>
  );
}
